import { BehaviorSubject, Observable } from 'rxjs';

/** @module ObservableState */


type SideEffect<T> = {
	callback: (prevState?: T) => void;
	/** A list of dependencies whose state values trigger the callback function, when the value is updated here */
	deps?: string[];
	/** A list of dependencies, which will force a state callback on update, even if their value is the same as the existing one already in the state */
	// alwaysTrigger?: string[];
}

type SideEffects<T> = Record<string, SideEffect<T>>

interface IObservableState<T> {
	set(stateUpdaterCallback: (state: T) => T): void
	get(): T
	runSideEffectsHandler(prevState: T, nextState: T): void
}

/**
 * This class build an observable store.
 * It is also in charge for handling side effects on state change,
 * @class module:ObservableState.set
 * @property {Function} set {@link module:ObservableState.ObservableState#set}
 * @property {Functio {@link module:ObservableState.ObservableSta}
 *
 */export default class ObservableState<T> implements IObservableState<T> {
	observedState: Observable<T>
	private _privateState: BehaviorSubject<any>
	sideEffects: SideEffects<T>


	/**
	 * Create a point.
	 * @param {T} stateToObserve - The state to observe.
	 * @param {SideEffects<T>} sideEffects - An array of sideEffects actions and deps for equality check trigger.
	 * If no deps are provided to the side effect, it will run everytime a state change occurs
	 */
	constructor(stateToObserve: T, sideEffects: SideEffects<T>) {
		this._privateState = new BehaviorSubject(stateToObserve);
		this.observedState = this._privateState.asObservable();
		this.sideEffects = sideEffects
	}

	/**
	 * @descripton This method is in charge of setting the values and trig the sideEffects
	 * @method module:ObservableState.ObservableState#set
	 */
	set(stateUpdaterCallback: (state: T) => T): void {

		const prevState = this.get()
		this._privateState.next(stateUpdaterCallback(prevState))

		this.runSideEffectsHandler(prevState, this._privateState.value)
	}

	/**
	* @descripton This method is in charge of handling the side effects after a state update
	* @method module:ObservableState.runSideEffectsHandler#runSideEffectsHandler
	*/
	runSideEffectsHandler(prevState: T, nextState: T): void {

		Object.values(this.sideEffects).forEach(sideEffect => {
			let shouldBeCalled = false

			const callSideEffect = () => {
				sideEffect.callback(this.get())
			}

			if (!sideEffect.deps) return callSideEffect()



			const verifySideEffectShouldBeCalled = (): boolean => {
				// const alwaysTrigger = sideEffect.alwaysTrigger || [];
				if (sideEffect.deps) {
					sideEffect.deps.forEach(dep => {
						// if (alwaysTrigger.indexOf(dep) > -1) {
						// 	// trigger callback regardless, even if value did not change
						// 	shouldBeCalled = true;
						// } else {
						// 	const currentDepValue = JSON.stringify(prevState[dep])
						// 	const nextDepValue = JSON.stringify(nextState[dep])
						// 	if (currentDepValue !== nextDepValue && !shouldBeCalled) shouldBeCalled = true
						// }
						const currentDepValue = JSON.stringify(prevState[dep])
						const nextDepValue = JSON.stringify(nextState[dep])
						if (currentDepValue !== nextDepValue && !shouldBeCalled) shouldBeCalled = true
					})
				}
				return shouldBeCalled
			}

			const shouldLaunchSideEffect = verifySideEffectShouldBeCalled()
			if (shouldLaunchSideEffect) callSideEffect()
		});
	}

	/**
	* @descripton This method return the observable state values
	* @method module:ObservableState.ObservableState#get
	*/
	get(): T {
		// return Object.freeze(JSON.parse(JSON.stringify(this._privateState.value))) as T
		return JSON.parse(JSON.stringify(this._privateState.value)) as T
	}
}
